import { useState } from 'react'

import { Box, Button, Divider, Link, Text } from '@factorialco/deprecated-design-system'
import { TransactionReconciliationQuery } from 'generated/resources/graphql'
import { sum } from 'lodash'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import {
  ReconciliationItem,
  ReconciliationSource,
  ReconciliationStatusEnum,
  ReconciliationType,
  useReconciliationForm,
} from 'modules/banking/hooks/useReconciliationForm'
import { invoiceType } from 'modules/banking/lib/invoicesHelpers'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

import { ReconciliationSuggestions } from './ReconciliationSuggestions'
import { ReconciliationSummary } from './ReconciliationSummary'
import { ReconciliationsInvoicesDataTable } from './ReconciliationsInvoicesDataTable'
import { SplitReconciledAmountsModal } from './SplitReconciledAmountsModal'
import { TagLabelComponent } from './TagLabelComponent'

interface Props {
  transaction: TransactionReconciliationQuery['banking']['transaction']
  loading: boolean
  onTransactionFullyReconciled: () => void
  onInvoiceDetailClick: (id: number, expensableId?: number) => void
  showSuggestions: boolean
  setShowSuggestions: (show: boolean) => void
}

export const NewReconciliationForm = ({
  transaction,
  onTransactionFullyReconciled,
  onInvoiceDetailClick,
  loading,
  showSuggestions,
  setShowSuggestions,
}: Props) => {
  const { access } = useCurrentSession()
  const locale = access.locale

  const [showSplitAmountsModal, setShowSplitAmountsModal] = useState<boolean>(false)
  const [showPartialPendingInvoiceModal, setShowPartialPendingInvoiceModal] =
    useState<boolean>(false)
  const [showPartialPendingTransactionModal, setShowPartialPendingTransactionModal] =
    useState<boolean>(false)
  const [showNoDocumentModal, setShowNoDocumentModal] = useState<boolean>(false)
  const [source, setSource] = useState<ReconciliationSource | undefined>(undefined)
  const [sourceReconciliations, setSourceReconciliations] = useState<ReconciliationItem[]>([])

  useUnsafeEffect(() => {
    if (!transaction) {
      setSource(undefined)
      setSourceReconciliations([])
    } else {
      const reconciliationStatus = transaction.fullyReconciledAt
        ? ReconciliationStatusEnum.Full
        : transaction.reconciliations.length
          ? ReconciliationStatusEnum.Partial
          : ReconciliationStatusEnum.None

      setSource({
        id: transaction.id,
        description: transaction.description || '',
        amountCentsToReconcile: Math.abs(transaction.amountCents),
        currency: transaction.currency,
        date: transaction.bookingDate,
        locale: locale,
        reconciliationStatus: reconciliationStatus,
        reconciliationType: ReconciliationType.Transaction,
        type: transaction.amountCents > 0 ? 'debit' : 'credit',
      })

      setSourceReconciliations(
        (transaction.reconciliations || [])
          .slice()
          .sort((a, b) => {
            return a.id - b.id
          })
          .filter((r) => r.reconciliationType !== 'skipped' && r.financialDocument)
          .map((r) => {
            const invoice = r.financialDocument
            if (!invoice) return null

            const amountCentsToReconcile = r.reconcilableReconciledAmountCents ?? 0
            return {
              reconciliationId: r.id,
              id: invoice.id,
              description: invoice.documentNumber || '-',
              secondaryDescription: contactFullName(invoice.vendor),
              amountCentsToReconcile,
              currency: invoice.currency || '',
              date: invoice.documentDate || '',
              locale: locale,
              type:
                invoice.totalAmountCents &&
                invoiceType(
                  true, // We are assuming all invoices are expenses invoices and not income invoices
                  invoice.totalAmountCents
                ),
            }
          })
          .filter((item): item is NonNullable<typeof item> => item !== null)
      )
    }
  }, [transaction, transaction?.reconciliations, locale])

  const handleOnTransactionFullyReconciled = () => {
    setSource(undefined)
    onTransactionFullyReconciled()
  }

  const {
    handleReconcileClick,
    handleNoDocumentReconcileClick,
    handleNoDocumentSubmitModal,
    handleSubmitSplitAmountsModal,
    handleSubmitPartialModal,
    removeReconciliation,
    addReconciliation,
    isSelectedNewReconciliation,
    dirtyForm,
    selectedReconciliations,
    newReconciliations,
    initialAmountPendingToBeReconciled,
    firstReconciliationAmountCents,
    firstReconciliationAmountCentsToReconcile,
  } = useReconciliationForm({
    source,
    sourceReconciliations,
    onTransactionFullyReconciled: handleOnTransactionFullyReconciled,
    setShowSplitAmountsModal,
    setShowPartialPendingInvoiceModal,
    setShowPartialPendingTransactionModal,
    setShowNoDocumentModal,
  })

  const canShowSuggestions =
    !!(transaction && transaction.reconciliationSuggestions.length > 0) &&
    !transaction.fullyReconciledAt &&
    !loading

  const footerModalButtons = (
    <>
      <Button
        type="secondary"
        onClick={() => {
          setShowPartialPendingInvoiceModal(false)
          setShowPartialPendingTransactionModal(false)
          setShowSplitAmountsModal(false)
        }}
      >
        {i18n.t('banking.reconciliations.common_modal.cancel')}
      </Button>
      <Button onClick={() => handleSubmitPartialModal()}>
        {i18n.t('banking.reconciliations.common_modal.confirm')}
      </Button>
    </>
  )

  return (
    <Box style={{ minWidth: '500px' }}>
      <Box flexDirection="column" gap="s16" overflowY="auto" style={{ scrollbarGutter: 'auto' }}>
        <Box paddingX="s16">
          <ReconciliationSummary
            source={source}
            reconciliations={selectedReconciliations}
            onRemove={removeReconciliation}
            loading={loading}
            locale={locale}
          />
        </Box>
        <Divider />
        {canShowSuggestions && showSuggestions && (
          <ReconciliationSuggestions
            transaction={transaction}
            onTransactionFullyReconciled={() => {
              onTransactionFullyReconciled()
            }}
            onHideSuggestions={() => setShowSuggestions(false)}
          />
        )}
        {(!canShowSuggestions || !showSuggestions) && (
          <>
            <Box
              paddingX="s16"
              gap="s12"
              style={{ minHeight: '300px', maxHeight: 'calc(100vh - 462px)' }}
              overflowY="auto"
            >
              <Box
                flexDirection="row"
                width="full"
                alignItems="center"
                justifyContent="spaceBetween"
              >
                <Text.Normal weight="semibold">
                  {i18n.t('banking.reconciliations.match_with_invoices')}
                </Text.Normal>
                {canShowSuggestions && (
                  <Link.Touchable onClick={() => setShowSuggestions(true)}>
                    {i18n.t('banking.reconciliations.suggestions.show')}
                  </Link.Touchable>
                )}
              </Box>
              <ReconciliationsInvoicesDataTable
                onInvoiceDetailClick={onInvoiceDetailClick}
                onSelect={addReconciliation}
                onRemove={removeReconciliation}
                isSelected={isSelectedNewReconciliation}
                disabled={!source}
                currency={transaction?.currency}
              />
            </Box>
            <Box paddingX="s16">
              <TagLabelComponent
                source={source}
                dirty={dirtyForm}
                onReconcile={handleReconcileClick}
                onNoDocument={handleNoDocumentReconcileClick}
              />
            </Box>
          </>
        )}
      </Box>
      {source && showPartialPendingTransactionModal && (
        <Modal
          onClose={() => setShowPartialPendingTransactionModal(false)}
          title={i18n.t('banking.reconciliations.partial_transaction_modal.title')}
          description={i18n.t('banking.reconciliations.partial_transaction_modal.subtitle', {
            partial: formattedAmount({
              amountCents: sum(selectedReconciliations.map((el) => el.amountCentsToReconcile)),
              currency: source.currency,
              locale: locale,
            }),
            total: formattedAmount({
              amountCents: source.amountCentsToReconcile,
              currency: source.currency,
              locale: locale,
            }),
          })}
          illustration="alert"
          footer={footerModalButtons}
        />
      )}
      {source &&
        showPartialPendingInvoiceModal &&
        firstReconciliationAmountCents &&
        firstReconciliationAmountCentsToReconcile && (
          <Modal
            onClose={() => setShowPartialPendingInvoiceModal(false)}
            title={i18n.t('banking.reconciliations.partial_invoice_modal.title')}
            description={i18n.t('banking.reconciliations.partial_invoice_modal.subtitle', {
              partial: formattedAmount({
                amountCents: firstReconciliationAmountCents,
                currency: source.currency,
                locale: locale,
              }),
              total: formattedAmount({
                amountCents: firstReconciliationAmountCentsToReconcile,
                currency: source.currency,
                locale: locale,
              }),
            })}
            illustration="alert"
            footer={footerModalButtons}
          />
        )}
      {source && showSplitAmountsModal && (
        <SplitReconciledAmountsModal
          type={ReconciliationType.Transaction}
          onClose={() => setShowSplitAmountsModal(false)}
          onSubmit={(items) => handleSubmitSplitAmountsModal(items)}
          selectedReconcilables={newReconciliations.map((reconciliation) => ({
            ...reconciliation,
            originalAmountCents: reconciliation.amountCentsToReconcile,
            updatedAmountCents: reconciliation.amountCentsToReconcile,
          }))}
          totalAmountCents={initialAmountPendingToBeReconciled}
          totalAmountCurrency={source.currency}
          locale={source.locale}
        />
      )}
      {source && showNoDocumentModal && (
        <Modal
          onClose={() => setShowNoDocumentModal(false)}
          title={i18n.t('banking.reconciliations.no_document_modal.title')}
          description={i18n.t('banking.reconciliations.no_document_modal.subtitle')}
          illustration="alert"
          footer={
            <>
              <Button type="secondary" onClick={() => setShowNoDocumentModal(false)}>
                {i18n.t('banking.reconciliations.common_modal.cancel')}
              </Button>
              <Button onClick={() => handleNoDocumentSubmitModal()}>
                {i18n.t('banking.reconciliations.common_modal.confirm')}
              </Button>
            </>
          }
        />
      )}
    </Box>
  )
}
